import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { PromiseToPayInitialState } from '../globalSlices/promiseToPaySlice';
import {
  getBuyerPromiseToPays,
  getSupplierPromiseToPays,
  createPromiseToPay,
  updatePromiseToPay,
  cancelPromiseToPay,
  getActivePromisesForInvoices,
  deletePromiseToPay,
} from '../services/promiseToPayService';
import { PromiseToPayWithUser } from '@dill/dill-shared';

export const getBuyerPromiseToPaysExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'getBuyerPromiseToPays';
  builder.addCase(getBuyerPromiseToPays.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getBuyerPromiseToPays.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.buyerPromiseToPays = action.payload.data as PromiseToPayWithUser[];
      }
    }
  );
  builder.addCase(getBuyerPromiseToPays.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierPromiseToPaysExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'getSupplierPromiseToPays';
  builder.addCase(getSupplierPromiseToPays.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierPromiseToPays.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.supplierPromiseToPays = action.payload.data as PromiseToPayWithUser[];
      }
    }
  );
  builder.addCase(getSupplierPromiseToPays.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createPromiseToPayExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'createPromiseToPay';
  builder.addCase(createPromiseToPay.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createPromiseToPay.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        const newPromise = action.payload.data as PromiseToPayWithUser;
        state.buyerPromiseToPays = [newPromise, ...state.buyerPromiseToPays];
        state.supplierPromiseToPays = [newPromise, ...state.supplierPromiseToPays];
      }
    }
  );
  builder.addCase(createPromiseToPay.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updatePromiseToPayExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'updatePromiseToPay';
  builder.addCase(updatePromiseToPay.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(updatePromiseToPay.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(updatePromiseToPay.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const cancelPromiseToPayExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'cancelPromiseToPay';
  builder.addCase(cancelPromiseToPay.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(cancelPromiseToPay.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(cancelPromiseToPay.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getActivePromisesForInvoicesExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'getActivePromisesForInvoices';
  builder.addCase(getActivePromisesForInvoices.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getActivePromisesForInvoices.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        const promises = action.payload.data as PromiseToPayWithUser[];
        // Build activePromisesByInvoice map
        const map: Record<string, PromiseToPayWithUser[]> = {};
        promises.forEach((promise) => {
          const linkedInvoiceIds = promise.linkedInvoiceIds || [];
          linkedInvoiceIds.forEach((invoiceId) => {
            if (!map[invoiceId]) {
              map[invoiceId] = [];
            }
            map[invoiceId].push(promise);
          });
        });
        state.activePromisesByInvoice = map;
      }
    }
  );
  builder.addCase(getActivePromisesForInvoices.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const deletePromiseToPayExtraReducer = (
  builder: ActionReducerMapBuilder<PromiseToPayInitialState>
) => {
  const reducerId = 'deletePromiseToPay';
  builder.addCase(deletePromiseToPay.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(deletePromiseToPay.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(deletePromiseToPay.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};
